import React, { useRef, useEffect } from "react";
import dateFormat, { i18n } from "dateformat";
import { useSelector } from "react-redux";

import { useAppDispatch } from "../../../../redux/store";
import { setDate } from "../../../../redux/halls/slice";
import { hallsSelector } from "../../../../redux/halls/selectors";

import styles from "./Navigate.module.scss";
import arrowLeftIcon from "../../../../assets/icons/arrowCalendarLeft.svg";
import arrowRightIcon from "../../../../assets/icons/arrowCalendarRight.svg";

import { dayNamesForRent, monthNamesForCalendar } from "../../../../resources/locale";
import { setDateToStorage } from "../../../../utils/storage/halls";

i18n.dayNames = dayNamesForRent;
i18n.monthNames = monthNamesForCalendar;

interface DayItem {
  date: Date;
  dayName: string;
  dayNumber: number;
  isSelected: boolean;
  isDisabled: boolean;
  dateString: string;
}

export const Navigation: React.FC = () => {
  const { date } = useSelector(hallsSelector);
  const dispatch = useAppDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedDayRef = useRef<HTMLButtonElement>(null);
  const leftSpacerRef = useRef<HTMLDivElement>(null);
  const rightSpacerRef = useRef<HTMLDivElement>(null);

  const selectedDate = new Date(Date.parse(date));
  const nowDate = new Date();
  nowDate.setHours(0, 0, 0, 0);

  const monthYear = dateFormat(selectedDate, "mmmm yyyy");

  const daysToShow = 7;
  const daysBefore = Math.floor(daysToShow / 2);
  const daysAfter = Math.floor(daysToShow / 2);

  const daysList: DayItem[] = React.useMemo(() => {
    const days: DayItem[] = [];
    
    for (let i = daysBefore; i > 0; i--) {
      const dayDate = new Date(selectedDate);
      dayDate.setDate(dayDate.getDate() - i);
      const dayDateNormalized = new Date(dayDate);
      dayDateNormalized.setHours(0, 0, 0, 0);
      
      days.push({
        date: dayDate,
        dayName: dateFormat(dayDate, "ddd").toUpperCase(),
        dayNumber: dayDate.getDate(),
        isSelected: false,
        isDisabled: dayDateNormalized < nowDate,
        dateString: dateFormat(dayDate, "isoDate"),
      });
    }

    const selectedDateNormalized = new Date(selectedDate);
    selectedDateNormalized.setHours(0, 0, 0, 0);
    
    days.push({
      date: selectedDate,
      dayName: dateFormat(selectedDate, "ddd").toUpperCase(),
      dayNumber: selectedDate.getDate(),
      isSelected: true,
      isDisabled: selectedDateNormalized < nowDate,
      dateString: dateFormat(selectedDate, "isoDate"),
    });

    for (let i = 1; i <= daysAfter; i++) {
      const dayDate = new Date(selectedDate);
      dayDate.setDate(dayDate.getDate() + i);
      const dayDateNormalized = new Date(dayDate);
      dayDateNormalized.setHours(0, 0, 0, 0);
      
      days.push({
        date: dayDate,
        dayName: dateFormat(dayDate, "ddd").toUpperCase(),
        dayNumber: dayDate.getDate(),
        isSelected: false,
        isDisabled: dayDateNormalized < nowDate,
        dateString: dateFormat(dayDate, "isoDate"),
      });
    }

    return days;
  }, [date, daysBefore, daysAfter, selectedDate, nowDate]);

  const canGoPrevWeek = React.useMemo(() => {
    const prevWeekDate = new Date(selectedDate);
    prevWeekDate.setDate(prevWeekDate.getDate() - 7);
    prevWeekDate.setHours(0, 0, 0, 0);
    return prevWeekDate >= nowDate;
  }, [selectedDate, nowDate]);

  const handlePrevWeek = () => {
    const prevWeekDate = new Date(selectedDate);
    prevWeekDate.setDate(prevWeekDate.getDate() - 7);
    const prevWeekDateString = dateFormat(prevWeekDate, "isoDate");
    setDateToStorage(prevWeekDateString);
    dispatch(setDate(prevWeekDateString));
  };

  const handleNextWeek = () => {
    const nextWeekDate = new Date(selectedDate);
    nextWeekDate.setDate(nextWeekDate.getDate() + 7);
    const nextWeekDateString = dateFormat(nextWeekDate, "isoDate");
    setDateToStorage(nextWeekDateString);
    dispatch(setDate(nextWeekDateString));
  };

  useEffect(() => {
    const updateSpacersAndScroll = () => {
      if (selectedDayRef.current && scrollContainerRef.current && leftSpacerRef.current && rightSpacerRef.current) {
        const container = scrollContainerRef.current;
        const selectedElement = selectedDayRef.current;
        const leftSpacer = leftSpacerRef.current;
        const rightSpacer = rightSpacerRef.current;
        
        const containerWidth = container.offsetWidth;
        const elementWidth = selectedElement.offsetWidth;
        
        const spacerWidth = Math.max(0, (containerWidth / 2) - (elementWidth / 2) - 4); 
        
        leftSpacer.style.width = `${spacerWidth}px`;
        rightSpacer.style.width = `${spacerWidth}px`;
        
        const elementOffsetLeft = selectedElement.offsetLeft;
        const scrollPosition = elementOffsetLeft - spacerWidth;
        
        container.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: "smooth",
        });
      }
    };

    const rafId = requestAnimationFrame(() => {
      setTimeout(updateSpacersAndScroll, 100);
    });
    
    window.addEventListener('resize', updateSpacersAndScroll);
    
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updateSpacersAndScroll);
    };
  }, [date]);

  const handleDayClick = (dayItem: DayItem) => {
    if (!dayItem.isDisabled) {
      setDateToStorage(dayItem.dateString);
      dispatch(setDate(dayItem.dateString));
    }
  };

  return (
    <div className={styles.navigate}>
      <div className={styles.navigate_header}>
        <button
          className={styles.navigate_button}
          disabled={!canGoPrevWeek}
          onClick={handlePrevWeek}
        >
          <img src={arrowLeftIcon} alt="prev week" />
        </button>
        <div className={styles.navigate_monthYear}>{monthYear}</div>
        <button
          className={styles.navigate_button}
          onClick={handleNextWeek}
        >
          <img src={arrowRightIcon} alt="next week" />
        </button>
      </div>
      <div className={styles.navigate_strip} ref={scrollContainerRef}>
        {daysList.map((dayItem, index) => (
          <button
            key={`${dayItem.dateString}-${index}`}
            ref={dayItem.isSelected ? selectedDayRef : null}
            className={`${styles.navigate_day} ${
              dayItem.isSelected ? styles.navigate_day_selected : ""
            } ${dayItem.isDisabled ? styles.navigate_day_disabled : ""}`}
            onClick={() => handleDayClick(dayItem)}
            disabled={dayItem.isDisabled}
          >
            <span className={styles.navigate_day_name}>{dayItem.dayName}</span>
            <span className={styles.navigate_day_number}>{dayItem.dayNumber}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
